import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Paper,
  Stack,
  Table,
  TableContainer,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import axios from "axios";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ExchangeRateDto,
  exchangeRateService,
} from "../../api/exchangeRateService";
import useAuth from "../../hooks/useAuth";

const newRate = () => ({
  id: "",
  year: new Date().getFullYear(),
  month: new Date().getMonth() + 1,
  sourceCurrency: "USD",
  targetCurrency: "MDL",
  rate: "",
});

const ExchangeRatesPage = () => {
  const { token } = useAuth();
  const headers = useMemo(
    () => ({ Authorization: `Bearer ${token}` }),
    [token],
  );
  const [rates, setRates] = useState<ExchangeRateDto[]>([]);
  const [form, setForm] = useState(newRate());
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const loadRates = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError("");
    try {
      const { data } = await exchangeRateService.getAll(headers);
      setRates(data);
    } catch {
      setError("Failed to load exchange rates.");
    } finally {
      setLoading(false);
    }
  }, [headers, token]);

  useEffect(() => void loadRates(), [loadRates]);

  const edit = (rate: ExchangeRateDto) => {
    setForm({ ...rate, rate: rate.rate.toString() });
    setOpen(true);
  };

  const save = async () => {
    const payload = {
      id: form.id,
      year: Number(form.year),
      month: Number(form.month),
      sourceCurrency: form.sourceCurrency.trim().toUpperCase(),
      targetCurrency: form.targetCurrency.trim().toUpperCase(),
      rate: Number(form.rate),
    };
    if (
      !payload.sourceCurrency ||
      !payload.targetCurrency ||
      payload.rate <= 0
    ) {
      setError("Currencies are required and the rate must be greater than 0.");
      return;
    }

    setSaving(true);
    setError("");
    try {
      if (payload.id) await exchangeRateService.update(payload, headers);
      else await exchangeRateService.create(payload, headers);
      setOpen(false);
      await loadRates();
    } catch (requestError) {
      if (
        axios.isAxiosError(requestError) &&
        requestError.response?.status === 409
      )
        setError("A rate already exists for this month and currency pair.");
      else setError("Failed to save exchange rate.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (rate: ExchangeRateDto) => {
    if (
      !window.confirm(
        `Delete ${rate.sourceCurrency}/${rate.targetCurrency} for ${rate.month}/${rate.year}?`,
      )
    )
      return;
    try {
      await exchangeRateService.delete(rate.id, headers);
      await loadRates();
    } catch {
      setError("Failed to delete exchange rate.");
    }
  };

  return (
    <Box>
      <Stack
        flexWrap={{ xs: "wrap", lg: "nowrap" }}
        useFlexGap
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 2 }}
      >
        <Typography variant="h5">Monthly Exchange Rates</Typography>
        <Button
          variant="contained"
          onClick={() => {
            setForm(newRate());
            setOpen(true);
          }}
        >
          Add Rate
        </Button>
      </Stack>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <Paper sx={{ p: 2 }}>
        {loading ? (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer
            sx={{
              maxWidth: "100%",
              overflowX: "auto",
              overscrollBehaviorX: "contain",
            }}
          >
            <Table size="small" sx={{ minWidth: 600 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Year</TableCell>
                  <TableCell>Month</TableCell>
                  <TableCell>Source</TableCell>
                  <TableCell>Target</TableCell>
                  <TableCell>Rate</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rates.map((rate) => (
                  <TableRow key={rate.id}>
                    <TableCell>{rate.year}</TableCell>
                    <TableCell>{rate.month}</TableCell>
                    <TableCell>{rate.sourceCurrency}</TableCell>
                    <TableCell>{rate.targetCurrency}</TableCell>
                    <TableCell>
                      {rate.rate.toLocaleString("en-US", {
                        maximumFractionDigits: 8,
                      })}
                    </TableCell>
                    <TableCell align="right">
                      <Button size="small" onClick={() => edit(rate)}>
                        Edit
                      </Button>
                      <Button
                        size="small"
                        color="error"
                        onClick={() => remove(rate)}
                      >
                        Delete
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {!rates.length && (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      No exchange rates have been added.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {form.id ? "Edit Exchange Rate" : "Add Exchange Rate"}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                type="number"
                label="Year"
                value={form.year}
                inputProps={{ min: 1 }}
                onChange={(e) =>
                  setForm((old) => ({ ...old, year: Number(e.target.value) }))
                }
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                type="number"
                label="Month"
                value={form.month}
                inputProps={{ min: 1, max: 12 }}
                onChange={(e) =>
                  setForm((old) => ({ ...old, month: Number(e.target.value) }))
                }
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Source Currency"
                value={form.sourceCurrency}
                inputProps={{ maxLength: 3 }}
                onChange={(e) =>
                  setForm((old) => ({
                    ...old,
                    sourceCurrency: e.target.value.toUpperCase(),
                  }))
                }
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Target Currency"
                value={form.targetCurrency}
                inputProps={{ maxLength: 3 }}
                onChange={(e) =>
                  setForm((old) => ({
                    ...old,
                    targetCurrency: e.target.value.toUpperCase(),
                  }))
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                type="number"
                label="Exchange Rate"
                value={form.rate}
                inputProps={{ min: 0.00000001, step: 0.00000001 }}
                onChange={(e) =>
                  setForm((old) => ({ ...old, rate: e.target.value }))
                }
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={
              saving ||
              Number(form.month) < 1 ||
              Number(form.month) > 12 ||
              Number(form.rate) <= 0
            }
            onClick={save}
          >
            {saving ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ExchangeRatesPage;
